






















































import { defineComponent, PropType, computed, ref, onMounted } from "@vue/composition-api";
import { ContentFile } from "@scrile/api-provider/dist/api/ContentFilesProvider";
import { PaymentPackage } from "@scrile/api-provider/dist/api/PaymentPackageProvider";
import { SubscriptionLevel } from "@scrile/api-provider/dist/api/SubscriptionLevelProvider";
import { ContentFeed } from "@scrile/api-provider/dist/projects/scrileConnect/ContentFeedProvider";
import useAuthUser from "@/hooks/useAuthUser";
import useNSFWUserSettings from "@/hooks/useNSFWUserSettings";
import { getElement } from "@/lib/elementHelpers";
import { isAudioFile, isMediaFile } from "@/lib/fileHelpers";
import AttachmentsInfo from "@/components/AttachmentsInfo";
import ContentFeedAudioList from "./components/ContentFeedAudioList";
import ContentFeedBaseView from "./components/ContentFeedBaseView";
import ContentFeedFileList from "./components/ContentFeedFileList";
import ContentFeedGallery from "./components/ContentFeedGallery";
import ContentFeedUnlockFeed from "./components/ContentFeedUnlockFeed";
import { ProcessedContentFiles } from "./";

export default defineComponent({
  name: "ContentFeedItem",
  components: {
    AttachmentsInfo,
    ContentFeedFileList,
    ContentFeedBaseView,
    ContentFeedAudioList,
    ContentFeedGallery,
    ContentFeedUnlockFeed,
  },
  props: {
    contentFeed: { type: Object as PropType<ContentFeed> },
    subscriptionLevels: { type: Array as PropType<SubscriptionLevel[]> },
    paymentPackages: { type: Array as PropType<PaymentPackage[]> },
    showFollowButton: { type: Boolean, default: false },
    showReportItem: { type: Boolean, default: true },
    showComment: { type: Boolean, default: true },
    showFooter: { type: Boolean, default: true },
    showShare: { type: Boolean, default: true },
    showPrice: { type: Boolean, default: true },
    pinnedPostId: { type: String as PropType<string | null>, default: null },
    enablePinPost: { type: Boolean, default: false },
    isFollowedUser: { type: Boolean },
  },
  setup(props, { emit }) {
    const { authUser } = useAuthUser();
    const { showNotSafeForWork } = useNSFWUserSettings();

    const contentFeedItem = ref();

    const isAuthor = computed(() => !!authUser.value && props.contentFeed?.user.id === authUser.value.id);

    const isFreePost = computed(() => {
      if (!props.contentFeed) return false;
      return props.contentFeed.pricePackageId === "0" && props.contentFeed.subscriptionLevelId === "0";
    });

    const hasAccessToFile = (f: ContentFile) => {
      return isAuthor.value || props.contentFeed?.purchased || f.publicAccess || isFreePost.value;
    };

    const processedFiles = computed(() => {
      return props.contentFeed?.files.reduce<{
        documents: ProcessedContentFiles;
        media: ProcessedContentFiles;
        audio: ProcessedContentFiles;
      }>(
        (out, f) => {
          const key = isMediaFile(f) ? "media" : isAudioFile(f) ? "audio" : "documents";
          const accessKey = hasAccessToFile(f) ? "accessed" : "locked";
          out[key].all.push(f);
          out[key][accessKey].push(f);
          return out;
        },
        {
          documents: { locked: [], accessed: [], all: [] },
          media: { locked: [], accessed: [], all: [] },
          audio: { locked: [], accessed: [], all: [] },
        }
      );
    });

    const allLockedFiles = computed(() => {
      if (!processedFiles.value) return;
      return {
        mediaLocked: processedFiles.value.media.locked,
        audioLocked: processedFiles.value.audio.locked,
        documentsLocked: processedFiles.value.documents.locked,
      };
    });

    const showContentFeedUnlock = computed(
      () => props.contentFeed && !isAuthor.value && !props.contentFeed.user.disabled && !props.contentFeed.purchased
    );

    const showFeedGallery = computed(() => {
      return (
        !!props.contentFeed &&
        !!processedFiles.value &&
        (processedFiles.value.media.all.length > 0 ||
          processedFiles.value.documents.locked.length > 0 ||
          processedFiles.value.audio.locked.length > 0)
      );
    });

    onMounted(() => emit("mounted", getElement(contentFeedItem.value)));

    return {
      showNotSafeForWork,
      contentFeedItem,
      processedFiles,
      allLockedFiles,
      showContentFeedUnlock,
      isAuthor,
      showFeedGallery,
    };
  },
});
